import { useState, useEffect } from 'react';
import { useAuth, getApiUrl } from '../hooks/useAuth';
import { toast, ToastContainer } from 'react-toastify';
import { formatDateToDDMMYYYY } from '../components/FormatedDate';

export default function BadgesPage() {
     const { user } = useAuth();
     const [badgeCycles, setBadgeCycles] = useState([]);
     const [loading, setLoading] = useState(true);
     const [receivingCycle, setReceivingCycle] = useState(null);
     const [technicianInfo, setTechnicianInfo] = useState(null);
     const [pagination, setPagination] = useState({
          page: 1,
          total_pages: 1,
          total: 0,
          pageSize: 10
     });

     const token = sessionStorage.getItem("token") || localStorage.getItem("token");

     useEffect(() => {
          console.log("Full user object:", user);
          console.log("Available user properties:", user ? Object.keys(user) : "No user");

          // Check for emp_code or other identifier
          const empCode = user?.emp_code || user?.employee_code || user?.user_code || user?.id;
          console.log("Found emp_code:", empCode);

          if (empCode) {
               console.log("Fetching badge cycles for emp_code:", empCode);
               fetchBadgeCycles(1, empCode);
          } else {
               console.log("No emp_code found, setting loading to false");
               setLoading(false);
               if (user) {
                    console.log("User exists but no emp_code. User properties:", Object.keys(user));
                    toast.error("Employee code not found in user profile");
               }
          }
     }, [user]);

     const fetchBadgeCycles = async (page = 1, empCode = null) => {
          const employeeCode = empCode || user?.emp_code || user?.employee_code || user?.user_code || user?.id;

          if (!employeeCode) {
               console.log("No emp_code available, stopping fetch");
               toast.error("Employee code not found");
               setLoading(false);
               return;
          }

          try {
               setLoading(true);
               const apiUrl = getApiUrl(`/badge_cycles/${employeeCode}?page=${page}`);
               console.log("Fetching from URL:", apiUrl);

               const response = await fetch(apiUrl, {
                    headers: {
                         'Authorization': `Bearer ${token}`,
                         'Content-Type': 'application/json'
                    }
               });

               console.log("Response status:", response.status);
               console.log("Response ok:", response.ok);

               if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
               }

               const data = await response.json();
               console.log("API Response:", data);

               if (data.status) {
                    setBadgeCycles(data.data || []);
                    setTechnicianInfo(data.technician_info || null);
                    setPagination({
                         page: data.page || 1,
                         total_pages: data.total_pages || 1,
                         total: data.total || 0,
                         pageSize: data.pageSize || 10
                    });
                    console.log("Data loaded successfully:", data.data?.length, "cycles");
               } else {
                    console.log("API returned status false:", data.message);
                    toast.error(data.message || "Failed to fetch badge cycles");
                    setBadgeCycles([]);
               }
          } catch (error) {
               console.error("Error fetching badge cycles:", error);
               toast.error(`Failed to fetch badge cycles: ${error.message}`);
               setBadgeCycles([]);
          } finally {
               setLoading(false);
          }
     }; const handleReceiveBadge = async (cycleId) => {
          try {
               setReceivingCycle(cycleId);
               const response = await fetch(
                    getApiUrl(`/badge_cycles/receive/${cycleId}`),
                    {
                         method: 'POST',
                         headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json'
                         },
                         body: JSON.stringify({
                              returned_by: user?.emp_code || user?.employee_code || user?.user_code || user?.id,
                              returned_at: new Date().toISOString()
                         })
                    }
               );

               const result = await response.json();

               if (result.status) {
                    toast.success("Badge received successfully!");
                    // Refresh current page with the current emp_code
                    const empCode = user?.emp_code || user?.employee_code || user?.user_code || user?.id;
                    fetchBadgeCycles(pagination.page, empCode);
               } else {
                    toast.error(result.message || "Failed to receive badge");
               }
          } catch (error) {
               console.error("Error receiving badge:", error);
               toast.error("Failed to receive badge");
          } finally {
               setReceivingCycle(null);
          }
     };

     const getStatusBadgeClass = (status) => {
          switch (status) {
               case 'ISSUED': return 'badge-warning';
               case 'RETURNED': return 'badge-success';
               case 'RECEIVED': return 'badge-info';
               case 'SENT_TO_VENDOR': return 'badge-primary';
               case 'ANALYZED': return 'badge-dark';
               default: return 'badge-secondary';
          }
     };

     const getStatusIcon = (status) => {
          switch (status) {
               case 'ISSUED': return 'fas fa-hand-holding';
               case 'RETURNED': return 'fas fa-check-circle';
               case 'RECEIVED': return 'fas fa-inbox';
               case 'SENT_TO_VENDOR': return 'fas fa-truck';
               case 'ANALYZED': return 'fas fa-microscope';
               default: return 'fas fa-question-circle';
          }
     };

     if (loading) {
          return (
               <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                    <div className="text-center">
                         <div className="spinner-border text-primary mb-3" role="status">
                              <span className="sr-only">Loading...</span>
                         </div>
                         <p className="text-muted">Loading badge cycles...</p>
                         {user && <small className="text-muted">User: {user.user_name} ({user.emp_code})</small>}
                    </div>
               </div>
          );
     }

     // If no user or no emp_code, show error message with manual input option
     if (!user || (!user.emp_code && !user.employee_code && !user.user_code && !user.id)) {
          return (
               <div className="container-fluid mt-5">
                    <div className="row justify-content-center">
                         <div className="col-md-6">
                              <div className="card shadow">
                                   <div className="card-body text-center">
                                        <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                                        <h4>Employee Code Required</h4>
                                        <p className="text-muted mb-4">
                                             {!user ? "User not authenticated" : "Employee code not found in profile"}
                                        </p>

                                        {user && (
                                             <div className="mb-4">
                                                  <p className="text-sm">
                                                       <strong>Current User:</strong> {user.user_name}
                                                  </p>
                                                  <p className="text-sm text-muted">
                                                       Available user properties: {Object.keys(user).join(', ')}
                                                  </p>
                                             </div>
                                        )}

                                        <div className="mb-3">
                                             <label className="form-label">Enter Your Employee Code:</label>
                                             <input
                                                  type="text"
                                                  className="form-control"
                                                  placeholder="e.g., 5000083"
                                                  id="manual-emp-code"
                                             />
                                        </div>

                                        <div className="d-flex gap-2 justify-content-center">
                                             <button
                                                  className="btn btn-primary"
                                                  onClick={() => {
                                                       const empCode = document.getElementById('manual-emp-code').value;
                                                       if (empCode.trim()) {
                                                            fetchBadgeCycles(1, empCode.trim());
                                                       } else {
                                                            toast.error("Please enter an employee code");
                                                       }
                                                  }}
                                             >
                                                  Load Badge Cycles
                                             </button>
                                             <button
                                                  className="btn btn-secondary"
                                                  onClick={() => window.location.reload()}
                                             >
                                                  Refresh Page
                                             </button>
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>
          );
     } return (
          <div>
               <ToastContainer position="top-right" />

               {/* Header */}
               <div className="d-sm-flex align-items-center justify-content-between mb-4">
                    <h1 className="h3 mb-0 text-gray-800">My TLD Badge Cycles</h1>
                    <div className="badge badge-info badge-lg">
                         {technicianInfo?.name || user?.user_name}
                         <br />
                         <small>Emp: {technicianInfo?.emp_code || user?.emp_code}</small>
                    </div>
               </div>

               {/* Summary Cards */}
               <div className="row mb-4">
                    <div className="col-xl-2 col-md-4 mb-4">
                         <div className="card border-left-warning shadow h-100 py-2">
                              <div className="card-body">
                                   <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                             <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                                                  Issued
                                             </div>
                                             <div className="h5 mb-0 font-weight-bold text-gray-800">
                                                  {badgeCycles.filter(cycle => cycle.status === 'ISSUED').length}
                                             </div>
                                        </div>
                                        <div className="col-auto">
                                             <i className="fas fa-hand-holding fa-2x text-gray-300"></i>
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </div>

                    <div className="col-xl-2 col-md-4 mb-4">
                         <div className="card border-left-success shadow h-100 py-2">
                              <div className="card-body">
                                   <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                             <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                                                  Returned
                                             </div>
                                             <div className="h5 mb-0 font-weight-bold text-gray-800">
                                                  {badgeCycles.filter(cycle => cycle.status === 'RETURNED').length}
                                             </div>
                                        </div>
                                        <div className="col-auto">
                                             <i className="fas fa-check-circle fa-2x text-gray-300"></i>
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </div>

                    <div className="col-xl-2 col-md-4 mb-4">
                         <div className="card border-left-info shadow h-100 py-2">
                              <div className="card-body">
                                   <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                             <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                                                  Received
                                             </div>
                                             <div className="h5 mb-0 font-weight-bold text-gray-800">
                                                  {badgeCycles.filter(cycle => cycle.status === 'RECEIVED').length}
                                             </div>
                                        </div>
                                        <div className="col-auto">
                                             <i className="fas fa-inbox fa-2x text-gray-300"></i>
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </div>

                    <div className="col-xl-2 col-md-4 mb-4">
                         <div className="card border-left-primary shadow h-100 py-2">
                              <div className="card-body">
                                   <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                             <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                                                  At Vendor
                                             </div>
                                             <div className="h5 mb-0 font-weight-bold text-gray-800">
                                                  {badgeCycles.filter(cycle => cycle.status === 'SENT_TO_VENDOR').length}
                                             </div>
                                        </div>
                                        <div className="col-auto">
                                             <i className="fas fa-truck fa-2x text-gray-300"></i>
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </div>

                    <div className="col-xl-2 col-md-4 mb-4">
                         <div className="card border-left-dark shadow h-100 py-2">
                              <div className="card-body">
                                   <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                             <div className="text-xs font-weight-bold text-dark text-uppercase mb-1">
                                                  Analyzed
                                             </div>
                                             <div className="h5 mb-0 font-weight-bold text-gray-800">
                                                  {badgeCycles.filter(cycle => cycle.status === 'ANALYZED').length}
                                             </div>
                                        </div>
                                        <div className="col-auto">
                                             <i className="fas fa-microscope fa-2x text-gray-300"></i>
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </div>

                    <div className="col-xl-2 col-md-4 mb-4">
                         <div className="card border-left-secondary shadow h-100 py-2">
                              <div className="card-body">
                                   <div className="row no-gutters align-items-center">
                                        <div className="col mr-2">
                                             <div className="text-xs font-weight-bold text-secondary text-uppercase mb-1">
                                                  Reports
                                             </div>
                                             <div className="h5 mb-0 font-weight-bold text-gray-800">
                                                  {badgeCycles.reduce((total, cycle) => total + (cycle.dose_reports?.length || 0), 0)}
                                             </div>
                                        </div>
                                        <div className="col-auto">
                                             <i className="fas fa-file-alt fa-2x text-gray-300"></i>
                                        </div>
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>

               {/* Badge Cycles Table */}
               <div className="card shadow mb-4">
                    <div className="card-header py-3">
                         <h6 className="m-0 font-weight-bold text-primary">
                              My Badge Cycles
                              <span className="badge badge-secondary ml-2">
                                   Total: {pagination.total}
                              </span>
                         </h6>
                    </div>
                    <div className="card-body">
                         {badgeCycles.length === 0 ? (
                              <div className="text-center py-4">
                                   <i className="fas fa-info-circle fa-3x text-gray-300 mb-3"></i>
                                   <p className="text-muted">No badge cycles found</p>
                              </div>
                         ) : (
                              <div className="table-responsive">
                                   <table className="table table-bordered table-sm table-striped">
                                        <thead className="thead-light">
                                             <tr>
                                                  <th style={{ fontSize: '12px', padding: '8px 6px' }}>Badge Code</th>
                                                  <th style={{ fontSize: '12px', padding: '8px 6px' }}>Branch</th>
                                                  <th style={{ fontSize: '12px', padding: '8px 6px' }}>Period</th>
                                                  <th style={{ fontSize: '12px', padding: '8px 6px' }}>Status</th>
                                                  <th style={{ fontSize: '12px', padding: '8px 6px' }}>Issued</th>
                                                  <th style={{ fontSize: '12px', padding: '8px 6px' }}>Returned</th>
                                                  <th style={{ fontSize: '12px', padding: '8px 6px' }}>Received</th>
                                                  <th style={{ fontSize: '12px', padding: '8px 6px' }}>Reports</th>
                                                  <th style={{ fontSize: '12px', padding: '8px 6px' }}>Action</th>
                                             </tr>
                                        </thead>
                                        <tbody>
                                             {badgeCycles.map((cycle) => (
                                                  <tr key={cycle.cycle_id} style={{ fontSize: '12px' }}>
                                                       <td style={{ padding: '6px', verticalAlign: 'middle' }}>
                                                            <strong style={{ fontSize: '11px' }}>{cycle.badge_code}</strong>
                                                       </td>
                                                       <td style={{ padding: '6px', verticalAlign: 'middle', fontSize: '11px' }}>
                                                            {cycle.branch_name}
                                                       </td>
                                                       <td style={{ padding: '6px', verticalAlign: 'middle' }}>
                                                            <span className="badge badge-light" style={{ fontSize: '10px' }}>
                                                                 {cycle.cycle_period} {cycle.year}
                                                            </span>
                                                       </td>
                                                       <td style={{ padding: '6px', verticalAlign: 'middle' }}>
                                                            <span className={`badge ${getStatusBadgeClass(cycle.status)}`} style={{ fontSize: '10px' }}>
                                                                 <i className={`${getStatusIcon(cycle.status)} mr-1`}></i>
                                                                 {cycle.status}
                                                            </span>
                                                       </td>
                                                       <td style={{ padding: '6px', verticalAlign: 'middle', fontSize: '10px' }}>
                                                            {cycle.issued_at ? (
                                                                 <div>
                                                                      <div style={{ fontWeight: 'bold' }}>{formatDateToDDMMYYYY(cycle.issued_at)}</div>
                                                                      <small className="text-muted" style={{ fontSize: '9px' }}>By: {cycle.issued_by}</small>
                                                                 </div>
                                                            ) : '--'}
                                                       </td>
                                                       <td style={{ padding: '6px', verticalAlign: 'middle', fontSize: '10px' }}>
                                                            {cycle.returned_at ? (
                                                                 <div>
                                                                      <div style={{ fontWeight: 'bold' }}>{formatDateToDDMMYYYY(cycle.returned_at)}</div>
                                                                      <small className="text-muted" style={{ fontSize: '9px' }}>By: {cycle.returned_by}</small>
                                                                 </div>
                                                            ) : '--'}
                                                       </td>
                                                       <td style={{ padding: '6px', verticalAlign: 'middle', fontSize: '10px' }}>
                                                            {cycle.received_at ? (
                                                                 <div>
                                                                      <div style={{ fontWeight: 'bold' }}>{formatDateToDDMMYYYY(cycle.received_at)}</div>
                                                                      <small className="text-muted" style={{ fontSize: '9px' }}>By: {cycle.received_by}</small>
                                                                 </div>
                                                            ) : '--'}
                                                       </td>
                                                       <td style={{ padding: '6px', verticalAlign: 'middle' }}>
                                                            {cycle.dose_reports && cycle.dose_reports.length > 0 ? (
                                                                 <div style={{ fontSize: '10px' }}>
                                                                      {cycle.dose_reports.map((report, index) => (
                                                                           <div key={report.report_id} className="d-flex align-items-center mb-1">
                                                                                {report.file_path?.toLowerCase().endsWith('.pdf') ? (
                                                                                     <i className="fas fa-file-pdf text-danger mr-1" style={{ fontSize: '10px' }}></i>
                                                                                ) : (
                                                                                     <i className="fas fa-file-alt text-primary mr-1" style={{ fontSize: '10px' }}></i>
                                                                                )}
                                                                                <a
                                                                                     href={
                                                                                          report.file_path?.startsWith('http')
                                                                                               ? report.file_path
                                                                                               : `${process.env.REACT_APP_BASE_URL}/${encodeURI(report.file_path?.replace(/^\/+/, '') || '')}`
                                                                                     }
                                                                                     target="_blank"
                                                                                     rel="noopener noreferrer"
                                                                                     className="btn btn-xs  mr-1"
                                                                                     style={{ fontSize: '9px', padding: '2px 4px' }}
                                                                                >
                                                                                     <i className="fas fa-eye"></i>
                                                                                </a>
                                                                                <small className="text-muted" style={{ fontSize: '9px' }}>
                                                                                     {report.dose_value_msv ? `${report.dose_value_msv}mSv` : ''}
                                                                                </small>
                                                                           </div>
                                                                      ))}
                                                                      <small className="text-muted" style={{ fontSize: '9px' }}>
                                                                           {cycle.dose_reports.length} report(s)
                                                                      </small>
                                                                 </div>
                                                            ) : (
                                                                 <span className="text-muted" style={{ fontSize: '10px' }}>
                                                                      <i className="fas fa-info-circle mr-1"></i>
                                                                      No reports
                                                                 </span>
                                                            )}
                                                       </td>
                                                       <td style={{ padding: '6px', verticalAlign: 'middle' }}>
                                                            {cycle.status === 'ISSUED' ? (
                                                                 <button
                                                                      className="btn btn-success btn-xs"
                                                                      onClick={() => handleReceiveBadge(cycle.cycle_id)}
                                                                      disabled={receivingCycle === cycle.cycle_id}
                                                                      style={{ fontSize: '10px', padding: '4px 8px' }}
                                                                 >
                                                                      {receivingCycle === cycle.cycle_id ? (
                                                                           <>
                                                                                <span className="spinner-border spinner-border-sm mr-1" style={{ width: '10px', height: '10px' }}></span>
                                                                                Receiving...
                                                                           </>
                                                                      ) : (
                                                                           <>
                                                                                <i className="fas fa-hand-paper mr-1"></i>
                                                                                Receive
                                                                           </>
                                                                      )}
                                                                 </button>
                                                            ) : (
                                                                 <span className="text-muted" style={{ fontSize: '10px' }}>
                                                                      <i className="fas fa-check mr-1"></i>
                                                                      {cycle.status}
                                                                 </span>
                                                            )}
                                                       </td>
                                                  </tr>
                                             ))}
                                        </tbody>
                                   </table>
                              </div>
                         )}

                         {/* Pagination */}
                         {pagination.total_pages > 1 && (
                              <nav className="mt-3">
                                   <ul className="pagination justify-content-center">
                                        <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                             <button
                                                  className="page-link"
                                                  onClick={() => fetchBadgeCycles(pagination.page - 1)}
                                                  disabled={pagination.page === 1}
                                             >
                                                  Previous
                                             </button>
                                        </li>

                                        {[...Array(pagination.total_pages)].map((_, index) => (
                                             <li key={index + 1} className={`page-item ${pagination.page === index + 1 ? 'active' : ''}`}>
                                                  <button
                                                       className="page-link"
                                                       onClick={() => fetchBadgeCycles(index + 1)}
                                                  >
                                                       {index + 1}
                                                  </button>
                                             </li>
                                        ))}

                                        <li className={`page-item ${pagination.page === pagination.total_pages ? 'disabled' : ''}`}>
                                             <button
                                                  className="page-link"
                                                  onClick={() => fetchBadgeCycles(pagination.page + 1)}
                                                  disabled={pagination.page === pagination.total_pages}
                                             >
                                                  Next
                                             </button>
                                        </li>
                                   </ul>
                              </nav>
                         )}
                    </div>
               </div>
          </div>
     );
}